import { BackfillHistoryDialog } from '../analytics/BackfillHistoryDialog';
import dialogStyles from './PaymentsSettingsTab.module.css';
import { istDateTimeInput, istDateInput, formatIstDateTime } from '../payments/paymentDates';
import { useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutationHook } from '../../hooks/useMutationHook';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPaymentTerms,
  PAYMENT_MESSAGES,
  getPaymentRebuildStatus,
  mapPaymentError,
  rebuildOrderMoney,
  updatePaymentTerms,
} from '../../api/endpoints/payments';
import type { GstMode, PaymentTerms, PaymentRebuildResult } from '../../api/types';
import { useOpsUser } from '../ownership/opsUser';
import styles from './SettingsView.module.css';
import { validatePaymentTerms } from './opsSettingsValidation';
import { DatePicker } from '../../components/ui/DatePicker';
import { Button } from '../../components/ui/Button';
import formStyles from '../../components/domain/FormModal.module.css';
import { DialogHelp } from '../../help/DialogHelp';

const PAYOUT_FIELDS: Array<[keyof PaymentTerms, string, number, number, string, string]> = [
  [
    'partnerAdvancePct',
    'Partner advance cap',
    0,
    100,
    '%',
    'Percentage of contracted freight payable as trip advance upon dispatch.',
  ],
  [
    'partnerAdvanceSlaMinutes',
    'Partner advance SLA',
    15,
    10080,
    'min',
    'minutes from loading — target duration from vehicle loaded timestamp to advance disbursement.',
  ],
  [
    'partnerBalanceSlaHours',
    'Partner balance SLA',
    1,
    720,
    'h',
    'Target duration from POD verification to final balance disbursement.',
  ],
  [
    'payoutApprovalTargetMinutes',
    'Payout approval SLA target',
    5,
    1440,
    'min',
    'Ops Manager approval target for pending payout requests.',
  ],
];
const INVOICE_FIELDS: typeof PAYOUT_FIELDS = [
  [
    'invoiceTargetHours',
    'Invoice generation SLA',
    1,
    720,
    'h',
    'Target duration to issue tax invoice after delivery confirmation.',
  ],
  [
    'defaultShipperPaymentTermsDays',
    'Default payment terms',
    0,
    180,
    'days',
    'Default invoice due date offset if not specified in shipper profile.',
  ],
];

function RouteLeaveGuard({ dirty }: { dirty: boolean }) {
  useBlocker(() => dirty && !window.confirm('Discard unsaved payment settings and leave?'));
  return null;
}

function pairs(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}
function sameTerms(left: PaymentTerms | null, right: PaymentTerms | null): boolean {
  if (!left || !right) return left === right;
  const normalized = (value: PaymentTerms) =>
    JSON.stringify(
      Object.entries(value)
        .filter(([key]) => key !== 'rebuildRecommended')
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => [
          key,
          key === 'invoiceGstOptions'
            ? pairs(String(item))
                .map((pair) => pair.toLowerCase())
                .sort()
                .join(',')
            : item,
        ]),
    );
  return normalized(left) === normalized(right);
}

function pairLabel(value: string) {
  const [mode, rate] = value.split(':');
  return `${mode} ${rate} %`;
}

export function PaymentsSettingsTab({
  onDirtyChange,
}: {
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const [currentRun, setCurrentRun] = useState<string | null>(null);
  const { isManager } = useOpsUser();
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['payments', 'settings'], queryFn: getPaymentTerms });
  const [rebuildRecommended, setRebuildRecommended] = useState(false);
  const saving = useRef(false);
  const [backfill, setBackfill] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [maintenanceConflict, setMaintenanceConflict] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const observedRunning = useRef(false);
  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(false), 30_000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);
  const rebuildStatus = useQuery({
    queryKey: ['payment-rebuild'],
    queryFn: ({ signal }) => getPaymentRebuildStatus(signal),
    enabled: isManager,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    refetchInterval: (query) =>
      rebuildOpen && query.state.data?.status === 'RUNNING' ? 3000 : false,
    refetchIntervalInBackground: false,
  });
  useEffect(() => {
    if (rebuildOpen && rebuildStatus.data?.status === 'RUNNING' && rebuildStatus.data.startedAt) {
      setCurrentRun(rebuildStatus.data.startedAt);
    }
  }, [rebuildOpen, rebuildStatus.data]);
  const running = rebuildStatus.data?.status === 'RUNNING';
  const rebuild = useMutation({
    mutationFn: rebuildOrderMoney,
    onMutate: () => {
      setMaintenanceConflict(false);
      setCurrentRun(null);
    },
    onSuccess: async (result) => {
      await client.cancelQueries({ queryKey: ['payment-rebuild'] });
      observedRunning.current = true;
      setCurrentRun(result.startedAt);
      client.setQueryData<PaymentRebuildResult>(['payment-rebuild'], {
        status: 'RUNNING',
        startedAt: result.startedAt,
        finishedAt: null,
        refreshed: 0,
        creditRepaired: 0,
      });
    },
    onError: (error) => {
      if ((error as { code?: string }).code === 'REBUILD_IN_PROGRESS') {
        setMaintenanceConflict(true);
        setCooldown(true);
        void rebuildStatus.refetch();
      }
    },
  });
  useEffect(() => {
    if (rebuildStatus.data?.status === 'RUNNING') observedRunning.current = true;
    if (rebuildStatus.data?.status === 'DONE' && observedRunning.current) {
      observedRunning.current = false;
      setCooldown(false);
      void client.invalidateQueries({ queryKey: ['payments'] });
    }
    if (rebuildStatus.data?.status === 'FAILED') observedRunning.current = false;
  }, [client, rebuildStatus.data]);
  const statusText = running
    ? `Running…${rebuildStatus.data?.startedAt ? ` started ${istDateTimeInput(new Date(rebuildStatus.data.startedAt)).slice(11)} (IST)` : ''}`
    : rebuildStatus.data?.status === 'DONE'
      ? `Done: ${rebuildStatus.data.refreshed} orders refreshed, ${rebuildStatus.data.creditRepaired} credits repaired`
      : null;
  const [form, setForm] = useState<PaymentTerms | null>(null);
  const [saved, setSaved] = useState<PaymentTerms | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newMode, setNewMode] = useState<GstMode>('FCM');
  const [newRate, setNewRate] = useState('');
  const dirty = isManager && !!form && !!saved && !sameTerms(form, saved);
  const serverChanged = dirty && !!query.data && !sameTerms(query.data, saved);
  // Clean drafts follow the server; edited drafts retain their saved baseline.
  useEffect(() => {
    if (query.data && !saving.current && !dirty && !sameTerms(query.data, saved)) {
      setForm(query.data);
      setSaved(query.data);
    }
  }, [query.data, dirty, saved]);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);
  const mutation = useMutationHook(updatePaymentTerms, {
    successMessage: 'Payment settings saved',
    errorMessage: mapPaymentError,
    invalidateKeys: [['payments']],
    onSuccess: (value) => {
      saving.current = false;
      setRebuildRecommended(value.rebuildRecommended === true);
      // Cancel any pre-save or invalidation refetch before installing the authoritative save response.
      void client.cancelQueries({ queryKey: ['payments', 'settings'] });
      client.setQueryData(['payments', 'settings'], value);
      setForm(value);
      setSaved(value);
      setError(null);
    },
    onError: (error) => {
      saving.current = false;
      setError(mapPaymentError(error));
    },
  });
  if (query.isError && !form) return <p role="alert">{mapPaymentError(query.error)}</p>;
  if (query.isLoading || !form) return <p>{t('settings.paymentsSettings.loading')}</p>;
  const defaultPair = `${form.defaultInvoiceGstMode}:${form.defaultInvoiceGstRate}`;
  const validationError = validatePaymentTerms(form);
  const remove = (pair: string) => {
    if (pair.toLowerCase() === defaultPair.toLowerCase()) {
      setError('Choose another default GST option before removing the default GST option.');
      return;
    }
    if (!window.confirm(`Remove ${pairLabel(pair)} from allowed GST options?`)) return;
    setError(null);
    setForm({
      ...form,
      invoiceGstOptions: pairs(form.invoiceGstOptions)
        .filter((item) => item !== pair)
        .join(','),
    });
  };
  const addPair = () => {
    if (!/^\d+(?:\.\d{1,2})?$/.test(newRate) || Number(newRate) < 0 || Number(newRate) > 40) {
      setError('Enter a GST rate from 0 to 40 with at most 2 decimals.');
      return;
    }
    const next = `${newMode}:${Number(newRate)}`;
    const currentPairs = pairs(form.invoiceGstOptions);
    if (
      !currentPairs.some((item) => item.toLowerCase() === next.toLowerCase()) &&
      currentPairs.length >= 6
    ) {
      setError('You can configure at most six GST options.');
      return;
    }
    if (currentPairs.some((item) => item.toLowerCase() === next.toLowerCase())) {
      setError('This GST option is already allowed.');
      return;
    }
    setForm({ ...form, invoiceGstOptions: [...currentPairs, next].join(',') });
    setNewRate('');
    setError(null);
  };
  const fields = (items: typeof PAYOUT_FIELDS) =>
    items.map(([key, label, min, max, unit, helper]) => (
      <div key={key}>
        <label className={styles.label} htmlFor={`payment-${key}`}>
          {label}
        </label>
        <div className={styles.inputWrap}>
          <input
            id={`payment-${key}`}
            aria-describedby={`help-${key}`}
            className={styles.input}
            type="number"
            min={min}
            max={max}
            disabled={!isManager || mutation.isLoading}
            value={Number.isNaN(Number(form[key])) ? '' : Number(form[key])}
            onChange={(event) =>
              setForm({
                ...form,
                [key]: event.target.value === '' ? NaN : Number(event.target.value),
              })
            }
          />
          <span className={styles.pctSuffix}>{unit}</span>
        </div>
        <p id={`help-${key}`} className={dialogStyles.helper}>
          {helper}
        </p>
      </div>
    ));
  const runResult = currentRun !== null && rebuildStatus.data?.startedAt === currentRun;
  return (
    <div
      className={isManager ? dialogStyles.page : `${dialogStyles.page} ${dialogStyles.readOnly}`}
    >
      {dataRouter && <RouteLeaveGuard dirty={dirty} />}
      <header className={styles.cardHeader}>
        <div>
          <h2>{t('settings.paymentsSettings.title')}</h2>
          <p className={styles.cardHint}>
            {t('settings.paymentsSettings.hint')}
          </p>
        </div>
        {isManager && (
          <div className={dialogStyles.headerActions}>
            {dirty && <span role="status">{t('settings.paymentsSettings.unsavedChanges')}</span>}
            <Button
              variant="secondary"
              size="sm"
              disabled={!dirty || mutation.isLoading}
              onClick={() => {
                setForm(query.data ?? saved);
                setSaved(query.data ?? saved);
                setError(null);
                setNewRate('');
              }}
            >
              {t('settings.paymentsSettings.discard')}
            </Button>
            <button
              className={styles.submit}
              disabled={
                !dirty ||
                mutation.isLoading ||
                serverChanged ||
                Boolean(validationError) ||
                Boolean(form.paymentsStartDate && form.paymentsStartDate > istDateInput())
              }
              onClick={() => {
                setError(null);
                saving.current = true;
                const settings = { ...form };
                delete settings.rebuildRecommended;
                mutation.mutate(settings);
              }}
            >
              {mutation.isLoading ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </header>
      {!isManager && <p className={dialogStyles.banner}>{t('settings.paymentsSettings.viewOnly')}</p>}
      {serverChanged && (
        <p role="status" className={dialogStyles.banner}>
          {t('settings.paymentsSettings.serverChanged')}
        </p>
      )}
      <section className={styles.card} aria-labelledby="payout-settings-title">
        <h3 id="payout-settings-title" className={styles.cardTitle}>
          {t('settings.paymentsSettings.payoutTitle')}
        </h3>
        <div className={dialogStyles.grid}>
          {fields(PAYOUT_FIELDS)}
          <div>
            <label className={styles.label} htmlFor="payments-start-date">
              {t('settings.paymentsSettings.paymentsStartDateLabel')}
            </label>
            <DatePicker
              id="payments-start-date"
              className={styles.input}
              max={istDateInput()}
              disabled={!isManager || mutation.isLoading}
              value={form.paymentsStartDate ?? ''}
              onChange={(next) => setForm({ ...form, paymentsStartDate: next || null })}
            />
            <p className={dialogStyles.helper}>
              {t('settings.paymentsSettings.paymentsStartDateHelp')}
            </p>
          </div>
        </div>
      </section>
      <section className={styles.card} aria-labelledby="invoice-settings-title">
        <h3 id="invoice-settings-title" className={styles.cardTitle}>
          {t('settings.paymentsSettings.invoiceTitle')}
        </h3>
        <div className={dialogStyles.grid}>
          {fields(INVOICE_FIELDS)}
          <div>
            <label className={styles.label} htmlFor="seller-state">
              {t('settings.paymentsSettings.sellerStateLabel')}
            </label>
            <input
              id="seller-state"
              aria-describedby="seller-state-help"
              className={styles.input}
              disabled={!isManager || mutation.isLoading}
              value={form.invoiceSellerStateCode}
              onChange={(event) => setForm({ ...form, invoiceSellerStateCode: event.target.value })}
            />
            <p id="seller-state-help" className={dialogStyles.helper}>
              {t('settings.paymentsSettings.sellerStateHelp')}
            </p>
          </div>
        </div>
      </section>
      <section className={styles.card} aria-labelledby="gst-settings-title">
        <h3 id="gst-settings-title" className={styles.cardTitle}>
          {t('settings.paymentsSettings.gstTitle')}
        </h3>
        <div className={dialogStyles.grid}>
          <div>
            <label className={styles.label} htmlFor="default-gst-mode">
              {t('settings.paymentsSettings.defaultGstModeLabel')}
            </label>
            <select
              id="default-gst-mode"
              aria-describedby="gst-mode-help"
              className={styles.input}
              disabled={!isManager || mutation.isLoading}
              value={form.defaultInvoiceGstMode}
              onChange={(event) =>
                setForm({ ...form, defaultInvoiceGstMode: event.target.value as GstMode })
              }
            >
              <option value="FCM">{t('settings.paymentsSettings.forwardCharge')}</option>
              <option value="RCM">{t('settings.paymentsSettings.reverseCharge')}</option>
            </select>
            <p id="gst-mode-help" className={dialogStyles.helper}>
              {t('settings.paymentsSettings.gstModeHelp')}
            </p>
          </div>
          {fields([
            [
              'defaultInvoiceGstRate',
              'Default GST rate',
              0,
              40,
              '%',
              'The default mode and rate must be an allowed GST option.',
            ],
          ])}
        </div>
        <h4>{t('settings.paymentsSettings.allowedGstTitle')}</h4>
        <div className={dialogStyles.chips}>
          {pairs(form.invoiceGstOptions).map((pair) => (
            <span key={pair} className={dialogStyles.chip}>
              <span>{pairLabel(pair)}</span>
              {isManager && (
                <button
                  type="button"
                  disabled={mutation.isLoading}
                  aria-label={`Remove ${pairLabel(pair)}`}
                  onClick={() => remove(pair)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
        <p className={dialogStyles.helper}>
          {t('settings.paymentsSettings.allowedGstHelp')}
        </p>
        {isManager && (
          <div className={dialogStyles.addOption}>
            <label className={styles.label}>
              {t('settings.paymentsSettings.modeLabel')}
              <select
                className={styles.input}
                aria-label={t('settings.paymentsSettings.newGstModeLabel')}
                disabled={mutation.isLoading}
                value={newMode}
                onChange={(event) => setNewMode(event.target.value as GstMode)}
              >
                <option value="FCM">{t('settings.paymentsSettings.modeFcm')}</option>
                <option value="RCM">{t('settings.paymentsSettings.modeRcm')}</option>
              </select>
            </label>
            <div>
              <label className={styles.label} htmlFor="new-gst-rate">
                {t('settings.paymentsSettings.rateLabel')}
              </label>
              <div className={styles.inputWrap}>
                <input
                  id="new-gst-rate"
                  className={styles.input}
                  inputMode="decimal"
                  disabled={mutation.isLoading}
                  value={newRate}
                  onChange={(event) => setNewRate(event.target.value)}
                />
                <span className={styles.pctSuffix}>%</span>
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={mutation.isLoading}
              onClick={addPair}
            >
              {t('settings.paymentsSettings.addOption')}
            </Button>
          </div>
        )}
      </section>
      <section className={styles.card}>
        <h3>{t('settings.paymentsSettings.payDnmTitle')}</h3>
        <p>{t('settings.paymentsSettings.payDnmHint')}</p>
        <div className={styles.grid}>
          {(
            [
              ['dnmPayeeAccountName', 'DNM account name'],
              ['dnmPayeeAccountNumber', 'DNM account number'],
              ['dnmPayeeIfsc', 'DNM IFSC'],
              ['dnmPayeeUpiId', 'DNM UPI ID'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className={styles.label}>
              {label}
              <input
                className={styles.input}
                value={form[key] ?? ''}
                disabled={!isManager || mutation.isLoading}
                onChange={(event) => setForm({ ...form, [key]: event.target.value })}
              />
            </label>
          ))}
        </div>
        <h3>{t('settings.paymentsSettings.appTargetsTitle')}</h3>
        <div className={styles.grid}>
          {fields([
            [
              'paymentClaimVerifyTargetHours',
              'Claim verification target',
              1,
              72,
              'h',
              'Target from shipper submission to verify or reject.',
            ],
            [
              'invoiceReminderDaysBefore',
              'Invoice reminder days before',
              0,
              15,
              'days',
              '0 disables due-soon reminders; overdue reminders continue.',
            ],
            [
              'chargeRequestDecideTargetHours',
              'Partner charge decision target',
              1,
              168,
              'h',
              'Target from partner request to manager decision.',
            ],
          ])}
        </div>
      </section>
      {(error || validationError) && (
        <div role="alert" className={styles.error}>
          {error ?? validationError}
        </div>
      )}
      {isManager && rebuildRecommended && (
        <div role="status" className={dialogStyles.banner}>
          {t('settings.paymentsSettings.rebuildRecommended')}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setRebuildRecommended(false);
              setCurrentRun(null);
              setRebuildOpen(true);
              void rebuildStatus.refetch();
            }}
          >
            {t('settings.paymentsSettings.runRebuild')}
          </Button>
        </div>
      )}
      {isManager && (
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="sm"
            disabled={backfill || rebuild.isPending}
            onClick={() => setBackfill(true)}
          >
            {t('settings.paymentsSettings.backfillHistory')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={
              rebuildOpen || rebuild.isPending || running || rebuildStatus.isLoading || backfill
            }
            onClick={() => {
              rebuild.reset();
              setCurrentRun(null);
              setRebuildOpen(true);
              void rebuildStatus.refetch();
            }}
          >
            {t('settings.paymentsSettings.rebuildOrderMoney')}
          </Button>
        </div>
      )}
      {isManager && !rebuildOpen && maintenanceConflict && (
        <p role="alert">{PAYMENT_MESSAGES.REBUILD_IN_PROGRESS}</p>
      )}
      {isManager && rebuildStatus.data && (
        <div>
          <p role="status">
            {rebuildStatus.data.status === 'IDLE' ? (
              'Last rebuild: never run'
            ) : (
              <>
                {t('settings.paymentsSettings.lastRebuild', {
                  status: rebuildStatus.data.status,
                  refreshed: rebuildStatus.data.refreshed,
                })}{' '}
                {rebuildStatus.data.finishedAt
                  ? `${formatIstDateTime(rebuildStatus.data.finishedAt)} IST`
                  : 'not finished'}
              </>
            )}
          </p>
          {!rebuildOpen && rebuildStatus.data.status === 'FAILED' && rebuildStatus.data.error && (
            <p role="status">{rebuildStatus.data.error}</p>
          )}
        </div>
      )}
      {isManager && !rebuildOpen && running && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setCurrentRun(null);
            setRebuildOpen(true);
            void rebuildStatus.refetch();
          }}
        >
          {t('settings.paymentsSettings.viewRebuildStatus')}
        </Button>
      )}
      {isManager && !rebuildOpen && rebuildStatus.isError && (
        <p role="alert">{mapPaymentError(rebuildStatus.error)}</p>
      )}
      {isManager && backfill && <BackfillHistoryDialog onClose={() => setBackfill(false)} />}
      {isManager && rebuildOpen && (
        <div className={dialogStyles.scrim}>
          <section
            className={dialogStyles.dialog}
            role="dialog"
            aria-modal="true"
            aria-label={t('settings.paymentsSettings.rebuildOrderMoney')}
          >
            <div className={formStyles.titleRow}>
              <h3>{t('settings.paymentsSettings.rebuildOrderMoney')}</h3>
              <DialogHelp context="settings-payments" title={t('settings.paymentsSettings.rebuildOrderMoney')} />
            </div>
            <p>{t('settings.paymentsSettings.rebuildDescription')}</p>
            {rebuild.isPending && <p role="status">{t('settings.paymentsSettings.startingRebuild')}</p>}
            {(running || runResult) && statusText && <p role="status">{statusText}</p>}
            {maintenanceConflict && <p role="alert">{PAYMENT_MESSAGES.REBUILD_IN_PROGRESS}</p>}
            {rebuild.isError && !maintenanceConflict && (
              <p role="alert">{mapPaymentError(rebuild.error)}</p>
            )}
            {rebuildStatus.isError && <p role="alert">{mapPaymentError(rebuildStatus.error)}</p>}
            {runResult && rebuildStatus.data?.status === 'FAILED' && (
              <p role="alert">{rebuildStatus.data.error}</p>
            )}
            <div className={styles.actions}>
              <Button
                variant="secondary"
                size="sm"
                disabled={rebuild.isPending}
                onClick={() => {
                  setRebuildOpen(false);
                  void client.cancelQueries({ queryKey: ['payment-rebuild'] });
                }}
              >
                {t('common.actions.close')}
              </Button>
              <button
                className={styles.submit}
                disabled={rebuild.isPending || running || cooldown || rebuildStatus.isFetching}
                onClick={() => rebuild.mutate()}
              >
                {t('settings.paymentsSettings.confirmRebuild')}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
